import {
	BusyIndicator,
	ItemSelectorLayout,
	NoResults,
	SearchInputForm
} from "client/Components";
import {
	ReactElement,
	Ref,
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useState
} from "react";
import { EmailBreadcrumb } from "./EmailBreadcrumb";
import { FolderList } from "./FolderList";
import { SelectedFolder, useFolderChildren } from "./useFolderChildren";
import { FolderNavigationStack, useFolderNavigationStack } from "./useFolderNavigationStack";
import { isNotNilOrEmpty } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid, Guid } from "client_legacy/ndaw/guid";

export type FolderSelectorProps = {
	mailbox: Mailbox | undefined;
	onChange(folder: SelectedFolder | undefined): void;
};

export type FolderSelectorHandle = {
	navigateForwardTo(folder: SelectedFolder): void;
};

export const FolderSelector = forwardRef(
	(props: FolderSelectorProps, ref: Ref<FolderSelectorHandle>): ReactElement => {
		const {
			mailbox,
			onChange
		} = props;

		const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>(
			() => ({ value: "", version: newGuid() })
		);
		const stack: FolderNavigationStack<SelectedFolder> = useFolderNavigationStack<SelectedFolder>();
		const currentFolder: SelectedFolder | undefined = stack.items[stack.items.length - 1];

		const [isBusy, items] = useFolderChildren(currentFolder);

		const { reset: resetStack, navigateForwardTo } = stack;

		const searchTermChangeHandler = useCallback(
			(searchTerm: string, version: Guid): void => {
				setSearchTerm({
					value: searchTerm,
					version
				});
			},
			[]
		);

		useEffect(
			(): void => {
				resetStack();

				if (mailbox) {
					navigateForwardTo({
						id: undefined,
						smtpAddress: mailbox.smtpAddress,
						name: mailbox.name
					});
				}
			},
			[
				mailbox,
				resetStack,
				navigateForwardTo
			]
		);

		useEffect(
			(): void => {
				onChange(currentFolder);
			},
			[
				onChange,
				currentFolder
			]
		);

		useEffect(
			(): void => {
				hasImplicitDependencyOn(currentFolder?.id);

				setSearchTerm({
					value: "",
					version: newGuid()
				});
			},
			[currentFolder?.id]
		);

		useImperativeHandle(
			ref,
			() => ({
				navigateForwardTo,
			}),
			[navigateForwardTo]
		);

		const shouldRenderNoResultsScreen: boolean = !isBusy && items.length === 0;
		const hasSearchTerm: boolean = isNotNilOrEmpty(searchTerm.value);
		const shouldRenderNoSearchResultsScreen: boolean = shouldRenderNoResultsScreen && hasSearchTerm;
		const shouldRenderNoItemsScreen: boolean = shouldRenderNoResultsScreen && !hasSearchTerm;
		const shouldRenderList: boolean = items.length > 0;

		return (
			<ItemSelectorLayout>
				<ItemSelectorLayout.Header>
					<ItemSelectorLayout.Breadcrumb>
						<EmailBreadcrumb
							items={stack.items}
							onOpenFolder={(x) => stack.navigateBackTo(x.id)}
						/>
					</ItemSelectorLayout.Breadcrumb>
					<ItemSelectorLayout.Search>
						<SearchInputForm
							value={searchTerm}
							onChange={searchTermChangeHandler}
							placeholder={localize("_Search")}
						/>
					</ItemSelectorLayout.Search>
				</ItemSelectorLayout.Header>
				<ItemSelectorLayout.Content>
					{isBusy && (
						<BusyIndicator />
					)}
					{shouldRenderNoSearchResultsScreen && (
						<NoResults
							icon="magnifying-glass"
							title={localize("_NoSearchResults")}
							message={localize("_PleaseTrySearchingWithAnotherTerm")}
							height="fill"
						/>
					)}
					{shouldRenderNoItemsScreen && (
						<NoResults
							icon="folder-open"
							title={localize("_NoItemsToShow")}
							message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
							height="fill"
						/>
					)}
					{shouldRenderList && (
						<FolderList
							searchTerm={searchTerm.value}
							items={items}
							onOpen={stack.navigateForwardTo}
							currentFolder={currentFolder}
						/>
					)}
				</ItemSelectorLayout.Content>
			</ItemSelectorLayout>
		);
	}
);
FolderSelector.displayName = "FolderSelector";

export type Mailbox = {
	smtpAddress: string | null;
	name: string;
};
